import { ReferralCreateData, ReferralData, ReferralUpdateForm } from 'chaika-frontend-types/frontend-cms';
import { EntryTypeFieldType } from 'chaika-enums';

import { store } from '@store';
import { ReferralList } from '@store/models/SystemFields/Referral';
import { GroupedListParams } from '@store/abstract/GroupedList';
import { DateHelper } from '@utils/DateHelper';
import { ReferralAbstractService } from '@store/services/ReferralService';
import { MedicalCaseApi } from '@api/MedicalCaseApi';
import { ReferralApi } from '@api/ReferralApi';

class ReferralService extends ReferralAbstractService {
    public async create(referrals: ReferralCreateData[], createList?: boolean): Promise<ReferralData[]> {
        const { ehr } = store;

        const createdReferrals = await this.createReferral(
            referrals,
            {
                ehrId: ehr.id,
                planActualOnDate: DateHelper.getStartOfDay().toISOString(),
            },
            createList,
        );

        const referralList = ehr.fields.getValue<ReferralList>(EntryTypeFieldType.Referrals);

        referralList.add(createdReferrals);

        return createdReferrals;
    }

    public async update(id: string, params: ReferralUpdateForm): Promise<void> {
        const ehr = store.ehr;

        // @ts-expect-error
        const { service, ...data } = params;

        await this.updateReferral({ ehrId: ehr.id, groupReferralId: id, params: data, withDuration: true });
        const list = ehr.fields.getValue<ReferralList>(EntryTypeFieldType.Referrals);

        const updateData = !!data.compositeServices
            ? { compositeService: { compositeServices: data.compositeServices }, ...data }
            : (data ?? {});
        // @ts-expect-error
        list.update(id, !!service ? { service, ...updateData } : updateData);
    }

    public async updateOne(id: string, params: ReferralUpdateForm, entryId?: string): Promise<void> {
        await this.updateReferral({ ehrId: store.ehr.id, groupReferralId: id, params, entryId });
    }

    public async add(id: string, addedCount: number, medicalCaseId: string): Promise<void> {
        const {
            ehr,
            ehr: { medicalCases },
        } = store;

        const newIds = await this.addReferrals(id, addedCount, ehr.id, medicalCaseId);

        const referralList = ehr.fields.getValue<ReferralList>(EntryTypeFieldType.Referrals);
        referralList.addItems(id, newIds);

        const { ids } = this.getReferral(id);
        const medicalCasesWithReferral = medicalCases.getFilteredValues({ referralIds: ids });
        medicalCasesWithReferral.forEach(medicalCase => {
            medicalCase.addReferrals(newIds);
        });
    }

    public async remove(id: string, removingCount?: number) {
        const {
            ehr: { id: ehrId, medicalCases },
        } = store;

        const removingIds = await this.removeReferrals({ ehrId }, id, removingCount);
        this.removeFromList(id, removingIds);

        const medicalCasesWithReferral = medicalCases.getFilteredValues({ referralIds: removingIds });
        medicalCasesWithReferral.forEach(medicalCase => {
            medicalCase.removeReferrals(removingIds);
        });
    }

    public async dettach(medicalCaseId: string, referralGroupId: string, referringDoctorId: string, entryId?: string) {
        const referral = this.getReferral(referralGroupId);

        referral.coAuthorIds = referral.coAuthorIds.filter(id => id !== referringDoctorId);

        await ReferralApi.removeCoAuthor(referral.ehrId, [referral.id], referringDoctorId);
        await MedicalCaseApi.removeReferral(medicalCaseId, referral.id, entryId);
    }

    protected getReferral(id: string): GroupedListParams<ReferralData> {
        const ehr = store.ehr;

        const referralList = ehr.fields.getValue<ReferralList>(EntryTypeFieldType.Referrals);
        return referralList.getExisting(id);
    }

    protected getReferralList(): ReferralList {
        return store.ehr.fields.getValue<ReferralList>(EntryTypeFieldType.Referrals);
    }
}

export const referralService = new ReferralService();
